<script setup lang="ts">
/*
 * A task on the execution canvas: the shared node (ZhExecutionNode) with the portal's live parts — select
 * by pointer or keyboard, the member's avatar with its preview, the run tooltip, and Vue Flow's handles,
 * which carry the edges and sit invisibly under the handles the node draws.
 */
import { Play, UserRound } from '@lucide/vue'
import { Handle, Position } from '@vue-flow/core'
import { ZhExecutionNode, ZhTooltip } from '@zerohuman/ui'
import { computed } from 'vue'
import MemberAvatar from '@/components/MemberAvatar.vue'
import { type ExecutionGraphNode, executionNodeView } from '@/lib/executionGraph'
import { TASK_GRAPH_NODE_HEIGHT, TASK_GRAPH_NODE_WIDTH } from '@/lib/taskGraph'

const props = defineProps<{ data: ExecutionGraphNode }>()
const emit = defineEmits<{ select: [] }>()
const view = computed(() => executionNodeView(props.data))
const accessibleLabel = computed(
    () =>
        `${props.data.name}, ${props.data.label}, ${props.data.runCount} ${props.data.runCount === 1 ? 'run' : 'runs'}. Inspect task`,
)
function onKeydown(event: KeyboardEvent) {
    if (!['Enter', ' ', 'Spacebar'].includes(event.key)) return
    event.preventDefault()
    event.stopPropagation()
    emit('select')
}
</script>

<template>
    <div class="execution-node" :class="{ 'execution-node-selected': data.selected }" :data-tone="data.tone" :data-task-id="data.id"
        :style="{ width: `${TASK_GRAPH_NODE_WIDTH}px`, height: `${TASK_GRAPH_NODE_HEIGHT}px` }"
        role="button" tabindex="0" :aria-label="accessibleLabel" :aria-pressed="data.selected"
        @click.stop="emit('select')" @keydown="onKeydown">
        <Handle type="target" :position="Position.Left" :connectable="false" />
        <ZhExecutionNode :node="view">
            <template #avatar>
                <MemberAvatar v-if="data.member" :member="data.member" class="size-full" fallback-class="text-[9px]" />
                <UserRound v-else :size="14" aria-hidden="true" />
            </template>
            <template #badge>
                <ZhTooltip text="Starting task"><span class="execution-node-start"><Play :size="10" aria-label="Starting task" /></span></ZhTooltip>
            </template>
            <template #runs="{ text }">
                <ZhTooltip v-if="data.runCount" :text="`${data.runCount} runs · ${data.completedCount} completed · ${data.activeCount} current`"><span>{{ text }}</span></ZhTooltip>
                <template v-else>{{ text }}</template>
            </template>
        </ZhExecutionNode>
        <Handle type="source" :position="Position.Right" :connectable="false" />
    </div>
</template>

<style scoped>
.execution-node {
    position: relative;
    cursor: pointer;
    transition: outline-color .2s;
}
.execution-node-selected, .execution-node:hover, .execution-node:focus-visible { outline: 3px solid color-mix(in srgb, var(--electric-cyan) 30%, transparent); outline-offset: 3px; }
.execution-node-start { display: inline-flex; padding: 3px; color: var(--ink-3); }
/* Vue Flow's handles anchor the edges; the node draws the visible ones in its tone. */
.execution-node :deep(.vue-flow__handle) { width: 9px; height: 9px; min-width: 0; min-height: 0; border: 0; background: transparent; opacity: 0; }
</style>
